import type { AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";

type Requirement = NonNullable<AIEquipmentSuggestionOption["completionRequirements"]>[number];

/** Original draft findings are history. Never label their stale numerical
 * messages as live failures after the engineer changes equipment or settings. */
export function currentCompletionRequirements(
  original: Requirement[],
  equipment: Record<string, unknown>,
  errors: Array<{ name: string; message: string }>
): Requirement[] {
  const present: Record<string, boolean> = {
    motor: !!equipment.motor_id, mle: !!equipment.mle_id, sensor: !!equipment.sensor_id,
    vsd: equipment.use_vsd === false || !!equipment.vsd_id,
    switchboard: equipment.use_vsd === true || !!equipment.switchboard_id,
    step_up_transformer: !!equipment.up_trans_id, step_down_transformer: !!equipment.down_trans_id,
    ...Object.fromEntries(["pump", "intake", "seal", "cable"].map(role => [role,
      Array.isArray(equipment[`${role}s_order`]) && (equipment[`${role}s_order`] as unknown[]).length > 0]))
  };
  const missing = [...new Set(original.map(item => item.equipmentType))]
    .filter(role => present[role] === false)
    .map(role => ({ code: `missing_${role}`, equipmentType: role,
      message: `No ${role.replaceAll("_", " ")} is selected.`, action: "Select suitable equipment and recalculate." }));
  const seen = new Set<string>();
  return [...missing, ...errors.filter(error => {
    const key = `${error.name}:${error.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  }).map(error => ({ code: `sizing_${error.name}`, equipmentType: "design", message: error.message,
    action: "Review the current sizing validation." }))];
}
